<template>
    <div>
        <template v-if="requests !== null">
            <div class="flex items-center justify-between mb-4">
                <h1>{{ _mft('dns:requests.myDomains') }}</h1>

                <button-secondary
                    v-if="!isAdding"
                    type="button"
                    @click.prevent="isAdding = true"
                >
                    {{ _mft('dns:requests.addDomain') }}
                </button-secondary>
            </div>

            <p class="mb-4">
                {{ _mft('dns:visitGeneratedDomain') }}
                <b>{{ props.generatedDomain }}</b
                >.
            </p>
            <p>{{ _mft('dns:selectCustomDomainIntro') }}</p>
            <p v-html="_mft('dns:selectCustomDomainExamples')"></p>

            <ul class="mt-8 space-y-3">
                <li v-if="isAdding && !isSubmitting">
                    <card-sub>
                        <form @submit.prevent="onSubmit">
                            <div class="w-full">
                                <input
                                    type="text"
                                    class="block w-full p-2 border rounded-md"
                                    required
                                    pattern="^(?:[A-z0-9](?:[A-z0-9\-]{0,61}[A-z0-9])?)(?:\.[A-z0-9](?:[A-z0-9\-]{0,61}[A-z0-9])?)+$"
                                    v-model="domainToAdd.domain"
                                    :placeholder="
                                        _mft(
                                            'dns:requests.domain.add.placeholder'
                                        )
                                    "
                                    maxlength="255"
                                />
                            </div>

                            <p
                                v-if="domainToAdd.domain?.length > 1"
                                class="text-sm pt-2"
                                v-html="
                                    _mft('dns:requests.domain.add.footnote', {
                                        domainToAdd: domainToAdd.domain,
                                    })
                                "
                            ></p>

                            <div class="flex justify-between items-end">
                                <div>
                                    <div class="pt-2">
                                        <input
                                            type="checkbox"
                                            class="mr-1"
                                            v-model="
                                                domainToAdd.shouldValidateForSite
                                            "
                                            :id="`${uuid}-should-validate-for-site`"
                                        />
                                        <label
                                            :for="`${uuid}-should-validate-for-site`"
                                        >
                                            {{ _mft('dns:domain.useForSite') }}
                                        </label>
                                    </div>

                                    <div class="pt-2">
                                        <input
                                            type="checkbox"
                                            class="mr-1"
                                            v-model="
                                                domainToAdd.shouldValidateForEmail
                                            "
                                            :id="`${uuid}-should-validate-for-email`"
                                        />
                                        <label
                                            :for="`${uuid}-should-validate-for-email`"
                                        >
                                            {{ _mft('dns:domain.useForEmail') }}
                                        </label>
                                    </div>
                                </div>
                                <div class="flex space-x-2">
                                    <button-secondary
                                        @click.prevent="isAdding = false"
                                    >
                                        {{ _mft('shared:action.cancel') }}
                                    </button-secondary>
                                    <button-primary type="submit">
                                        {{
                                            _mft(
                                                'dns:requests.domain.add.button'
                                            )
                                        }}
                                    </button-primary>
                                </div>
                            </div>
                        </form>
                    </card-sub>
                </li>
                <li
                    v-else-if="(isAdding && isSubmitting) || loadingManaged"
                    class="flex flex-row items-center justify-between p-5 rounded-lg odd:bg-gray-100"
                >
                    <mf-spinner-medium class="w-16 h-8"></mf-spinner-medium>
                </li>

                <li v-for="d in managedDomains" :key="`managed-${d.id}`">
                    <card-sub>
                        <div
                            class="w-full flex flex-row justify-between items-baseline mb-1 flex-wrap gap-2"
                        >
                            <div class="flex flex-row gap-2 items-baseline">
                                <h6 class="mt-0 mb-0 font-bold text-lg">
                                    {{ d.domain }}
                                </h6>
                                <span
                                    class="inline-flex items-center rounded bg-brandPrimary-50 px-1.5 py-0.5 text-[10px] font-medium text-brandPrimary-700"
                                >
                                    Bought via myflow
                                </span>
                                <span
                                    v-if="isDomainInUseForSite(d.domain)"
                                    class="inline-flex items-center rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-800"
                                >
                                    {{
                                        _mft('dns:domain.inUseForWebsiteBadge')
                                    }}
                                </span>
                                <span
                                    v-if="isDomainInUseForEmail(d.domain)"
                                    class="inline-flex items-center rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-800"
                                >
                                    {{ _mft('dns:domain.inUseForEmailBadge') }}
                                </span>
                            </div>
                            <div class="flex flex-row gap-3">
                                <a
                                    :href="
                                        route(
                                            'settings.domains.manage.show',
                                            d.id
                                        )
                                    "
                                    class="rounded-md border bg-white px-3 py-1 text-xs font-medium hover:bg-gray-50"
                                >
                                    Manage
                                </a>
                            </div>
                        </div>
                        <div class="text-xs text-gray-500">
                            <span
                                v-if="d.status !== 'active'"
                                class="text-amber-700"
                                >Status: {{ d.status }}</span
                            >
                            <template v-else>
                                <div>
                                    Renews
                                    {{
                                        d.expires_at
                                            ? new Date(
                                                  d.expires_at
                                              ).toLocaleDateString()
                                            : '—'
                                    }}
                                </div>
                                <div class="mt-1 flex flex-wrap gap-1.5">
                                    <span
                                        v-if="d.site_validated"
                                        class="inline-flex items-center gap-1 rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-800"
                                    >
                                        <i
                                            class="fa-regular fa-circle-check"
                                        ></i>
                                        Website connected
                                    </span>
                                    <span
                                        v-else-if="d.site_activation_pending"
                                        class="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800"
                                    >
                                        <i class="fa-regular fa-clock"></i>
                                        Website awaiting DNS
                                    </span>
                                    <span
                                        v-else-if="d.site_activation_failed"
                                        class="inline-flex items-center gap-1 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-800"
                                    >
                                        <i
                                            class="fa-regular fa-circle-exclamation"
                                        ></i>
                                        Website verification timed out
                                        <button
                                            type="button"
                                            class="underline"
                                            :disabled="
                                                retryingManagedSiteDomainId ===
                                                d.id
                                            "
                                            @click="
                                                retryManagedSiteActivation(d)
                                            "
                                        >
                                            {{
                                                retryingManagedSiteDomainId ===
                                                d.id
                                                    ? 'Retrying…'
                                                    : 'Retry'
                                            }}
                                        </button>
                                    </span>
                                    <span
                                        v-if="d.email_validated"
                                        class="inline-flex items-center gap-1 rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-800"
                                    >
                                        <i
                                            class="fa-regular fa-circle-check"
                                        ></i>
                                        Email connected
                                    </span>
                                    <span
                                        v-else-if="d.email_activation_pending"
                                        class="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800"
                                    >
                                        <i class="fa-regular fa-clock"></i>
                                        Email setting up…
                                    </span>
                                    <span
                                        v-else-if="d.email_activation_failed"
                                        class="inline-flex items-center gap-1 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-800"
                                    >
                                        <i
                                            class="fa-regular fa-circle-exclamation"
                                        ></i>
                                        Email verification timed out
                                        <button
                                            type="button"
                                            class="underline"
                                            :disabled="
                                                retryingManagedEmailDomainId ===
                                                d.id
                                            "
                                            @click="
                                                retryManagedEmailActivation(d)
                                            "
                                        >
                                            {{
                                                retryingManagedEmailDomainId ===
                                                d.id
                                                    ? 'Retrying…'
                                                    : 'Retry'
                                            }}
                                        </button>
                                    </span>
                                </div>
                            </template>
                        </div>
                    </card-sub>
                </li>

                <li v-for="request in requests" :key="request.id">
                    <card-sub>
                        <div
                            class="w-full flex flex-row justify-between items-baseline mb-3 flex-wrap"
                        >
                            <div class="flex flex-row gap-1 items-baseline">
                                <h6 class="mt-0 mb-1 font-bold text-lg">
                                    {{ request.domain }}
                                </h6>
                                <span
                                    v-if="
                                        isDomainInUseForSite(
                                            request.domain_ascii
                                        )
                                    "
                                    class="inline-flex items-center rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-800"
                                >
                                    {{
                                        _mft('dns:domain.inUseForWebsiteBadge')
                                    }}
                                </span>
                                <span
                                    v-if="
                                        isDomainInUseForEmail(
                                            request.domain_ascii
                                        )
                                    "
                                    class="inline-flex items-center rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-800"
                                >
                                    {{ _mft('dns:domain.inUseForEmailBadge') }}
                                </span>
                                <span v-if="request.last_failed_at">
                                    {{
                                        _mft('dns:requests.lastAttempt', {
                                            time: request.formatted_last_failed_at,
                                        })
                                    }}
                                </span>
                            </div>

                            <div class="flex flex-row gap-3">
                                <button-secondary
                                    size="sm"
                                    v-if="!request.should_validate_for_site"
                                    :disabled="request.enabling_for_site"
                                    @click.prevent="onEnableForSite(request)"
                                >
                                    <div class="flex flex-row gap-3">
                                        <mf-spinner-medium
                                            v-if="request.enabling_for_site"
                                            class="w-[1em] h–[1em]"
                                        ></mf-spinner-medium>
                                        {{ _mft('dns:domain.useForSite') }}
                                    </div>
                                </button-secondary>

                                <button-secondary
                                    size="sm"
                                    v-if="!request.should_validate_for_email"
                                    :disabled="request.enabling_for_email"
                                    @click.prevent="onEnableForEmail(request)"
                                >
                                    <div class="flex flex-row gap-3">
                                        <mf-spinner-medium
                                            v-if="request.enabling_for_email"
                                            class="w-[1em] h–[1em]"
                                        ></mf-spinner-medium>
                                        {{ _mft('dns:domain.useForEmail') }}
                                    </div>
                                </button-secondary>

                                <button-primary
                                    size="sm"
                                    v-if="!request.is_validated"
                                    type="button"
                                    :class="`${
                                        request.validating
                                            ? 'animate-pulse'
                                            : ''
                                    }`"
                                    @click.prevent="
                                        onTriggerValidation(request)
                                    "
                                    :disabled="
                                        request.validating ||
                                        !request.is_available_for_validation
                                    "
                                >
                                    <i class="fa-regular fa-refresh"></i>
                                    <span class="sr-only">{{
                                        _mft('dns:requests.refresh')
                                    }}</span>
                                </button-primary>

                                <button-danger
                                    size="sm"
                                    v-if="request.is_deletable"
                                    type="button"
                                    :class="`${
                                        request.deleting ? 'animate-pulse' : ''
                                    }`"
                                    @click="onDelete(request)"
                                    :disabled="request.deleting"
                                >
                                    <i class="fa-regular fa-trash"></i>
                                    <span class="sr-only">{{
                                        _mft('dns:requests.delete')
                                    }}</span>
                                </button-danger>
                            </div>
                        </div>

                        <p v-if="request.is_base_validated" class="mb-2">
                            ✅ {{ _mft('dns:requests.approved') }}
                        </p>
                        <div v-else class="mb-2">
                            <h6 class="font-bold mt-6 mb-2">
                                {{
                                    _mft('dns:requests.validation.instruction')
                                }}
                            </h6>

                            <div
                                v-for="(
                                    record, recordIdx
                                ) in request.base_validation_records"
                                :key="recordIdx"
                                class="mb-3"
                            >
                                <domain-validation-record-instruction
                                    :record="record"
                                    :is-last-record="
                                        recordIdx ===
                                        request.base_validation_records.length -
                                            1
                                    "
                                ></domain-validation-record-instruction>
                            </div>
                        </div>

                        <p
                            v-if="
                                request.is_validated_for_site &&
                                isDomainInUseForSite(request.domain_ascii)
                            "
                            class="mb-2"
                        >
                            ✅
                            <mf-translate
                                domainkey="dns:domain.inUseForSite"
                                :args="{ domain: request.domain }"
                            ></mf-translate>
                        </p>
                        <p
                            v-else-if="request.is_validated_for_site"
                            class="mb-2"
                        >
                            ✅ {{ _mft('dns:domain.connectedForSite') }}
                        </p>
                        <div
                            v-else-if="request.should_validate_for_site"
                            class="mb-2"
                        >
                            <h6 class="font-bold mt-6 mb-2">
                                {{ _mft('dns:domain.toEnableForSite') }}
                            </h6>

                            <template
                                v-if="
                                    request.site_validation_records?.length > 0
                                "
                            >
                                <div
                                    v-for="(
                                        record, recordIdx
                                    ) in request.site_validation_records"
                                    :key="recordIdx"
                                    class="mb-3 flex"
                                >
                                    <div
                                        class="mt-0.5 mr-2 text-gray-400 text-base font-bold"
                                    >
                                        {{ recordIdx + 1 }}
                                    </div>
                                    <div class="w-full">
                                        <domain-validation-record-instruction
                                            :record="record"
                                            :is-last-record="
                                                recordIdx ===
                                                request.site_validation_records
                                                    .length -
                                                    1
                                            "
                                        ></domain-validation-record-instruction>
                                    </div>
                                </div>
                            </template>
                            <p v-else>
                                {{ _mft('dns:instructions.preparing') }}
                            </p>
                        </div>

                        <p
                            v-if="
                                request.is_validated_for_email &&
                                isDomainInUseForEmail(request.domain_ascii)
                            "
                            class="mb-2"
                        >
                            ✅ {{ _mft('dns:domain.inUseForEmail') }}
                        </p>
                        <p
                            v-else-if="request.is_validated_for_email"
                            class="mb-2"
                        >
                            ✅ {{ _mft('dns:domain.connectedForEmail') }}
                        </p>
                        <div
                            v-else-if="request.should_validate_for_email"
                            class="mb-2"
                        >
                            <h6 class="font-bold mt-6 mb-2">
                                {{ _mft('dns:domain.toEnableForEmail') }}
                            </h6>

                            <template
                                v-if="
                                    request.email_validation_records?.length > 0
                                "
                            >
                                <div
                                    v-for="(
                                        record, recordIdx
                                    ) in request.email_validation_records"
                                    :key="recordIdx"
                                    class="mb-3 flex"
                                >
                                    <div
                                        class="mt-0.5 mr-2 text-gray-400 text-base font-bold"
                                    >
                                        {{ recordIdx + 1 }}
                                    </div>
                                    <div class="w-full">
                                        <domain-validation-record-instruction
                                            :record="record"
                                            :is-last-record="
                                                recordIdx ===
                                                request.email_validation_records
                                                    .length -
                                                    1
                                            "
                                        ></domain-validation-record-instruction>
                                    </div>
                                </div>
                            </template>
                            <p v-else>
                                {{ _mft('dns:instructions.preparing') }}
                            </p>
                        </div>
                    </card-sub>
                </li>
            </ul>
        </template>
        <mf-spinner-medium v-else class="w-16 h-8"></mf-spinner-medium>
    </div>
</template>

<script setup>
import { onMounted, ref, reactive } from 'vue';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

const uuid = uuidv4();

const props = defineProps({
    generatedDomain: { type: String, default: null },
    customDomain: { type: String, default: null },
    emailSenderDomain: { type: String, default: null },
});

const normalizeDomain = (domain) =>
    domain?.trim().toLowerCase().replace(/\.$/, '') || null;
const normalizedCustomDomain = normalizeDomain(props.customDomain);
const normalizedEmailSenderDomain = normalizeDomain(props.emailSenderDomain);
const isDomainInUseForSite = (domain) =>
    normalizedCustomDomain !== null &&
    normalizeDomain(domain) === normalizedCustomDomain;
const isDomainInUseForEmail = (domain) =>
    normalizedEmailSenderDomain !== null &&
    normalizeDomain(domain) === normalizedEmailSenderDomain;

const sleep = (m) => new Promise((r) => setTimeout(r, m));

const dateFormatter = new Intl.DateTimeFormat(['sv-SE'], {
    timeZone: 'Europe/Stockholm',
    dateStyle: 'short',
    timeStyle: 'short',
});

const isAdding = ref(false);
const isSubmitting = ref(false);
const retryingManagedSiteDomainId = ref(null);
const retryingManagedEmailDomainId = ref(null);
const domainToAdd = reactive({
    domain: null,
    shouldValidateForSite: false,
    shouldValidateForEmail: false,
});
const requests = ref(null);
const managedDomains = ref([]);
const loadingManaged = ref(false);

const decorateRequest = (request) => ({
    ...request,
    formatted_last_failed_at: request.last_failed_at
        ? dateFormatter.format(new Date(request.last_failed_at))
        : null,
    validating: false,
    deleting: false,
    enabling_for_site: false,
    enabling_for_email: false,
});

const refreshRequests = async () => {
    const response = await axios.get(
        route('domain_validations.requests.index')
    );
    requests.value = response.data.data
        .map(decorateRequest)
        .sort((a, b) => b.id - a.id);
};

onMounted(() => {
    refreshRequests();
    fetchManaged();
})

const onSubmit = async () => {
    isSubmitting.value = true;

    try {
        const response = await axios.post(
            route('domain_validations.requests.store'),
            {
                domain: domainToAdd.domain,
                should_validate_for_site: domainToAdd.shouldValidateForSite,
                should_validate_for_email: domainToAdd.shouldValidateForEmail,
            }
        );
        domainToAdd.domain = null;
        domainToAdd.shouldValidateForSite = false;
        domainToAdd.shouldValidateForEmail = false;
        isAdding.value = false;
        requests.value.splice(0, 0, decorateRequest(response.data));
    } finally {
        isSubmitting.value = false;
    }
};

const onTriggerValidation = async (request) => {
    request.validating = true;
    try {
        await axios.post(
            route('domain_validations.requests.triggerValidation', request.id)
        );

        let waitTime = 1000;
        let newRequest = null;
        while (
            newRequest == null ||
            newRequest?.updated_at === request.updated_at
        ) {
            await sleep(waitTime);
            waitTime *= 1.5;

            const response = await axios.get(
                route('domain_validations.requests.show', request.id)
            );
            newRequest = response.data;
        }

        const index = requests.value.findIndex((r) => r.id === request.id);
        requests.value.splice(index, 1, decorateRequest(newRequest));
    } finally {
        request.validating = false;
    }
};

const onEnableForSite = async (request) => {
    request.enabling_for_site = true;
    try {
        const { data: newRequest } = await axios.post(
            route('domain_validations.requests.enableForSite', request.id)
        );

        const index = requests.value.findIndex((r) => r.id === request.id);
        requests.value.splice(index, 1, decorateRequest(newRequest));
    } finally {
        request.enabling_for_site = false;
    }
};

const onEnableForEmail = async (request) => {
    request.enabling_for_email = true;
    try {
        const { data: newRequest } = await axios.post(
            route('domain_validations.requests.enableForEmail', request.id)
        );

        const index = requests.value.findIndex((r) => r.id === request.id);
        requests.value.splice(index, 1, decorateRequest(newRequest));
    } finally {
        request.enabling_for_email = false;
    }
};

const retryManagedEmailActivation = async (domain) => {
    retryingManagedEmailDomainId.value = domain.id;
    try {
        await axios.post(
            route('settings.domains.manage.retry-email-activation', domain.id)
        );
        domain.email_activation_pending = true;
        domain.email_activation_failed = false;
    } finally {
        retryingManagedEmailDomainId.value = null;
    }
};

const retryManagedSiteActivation = async (domain) => {
    retryingManagedSiteDomainId.value = domain.id;
    try {
        await axios.post(
            route('settings.domains.manage.retry-site-activation', domain.id)
        );
        domain.site_activation_pending = true;
        domain.site_activation_failed = false;
    } finally {
        retryingManagedSiteDomainId.value = null;
    }
};

const onDelete = async (request) => {
    request.deleting = true;
    try {
        await axios.delete(
            route('domain_validations.requests.destroy', request.id)
        );
        requests.value = requests.value.filter((r) => r.id !== request.id);
    } finally {
        request.deleting = false;
    }
};

const fetchManaged = async () => {
    loadingManaged.value = true;
    try {
        const { data } = await axios.get(route('settings.domains.managed.list'));
        managedDomains.value = data.domains || [];
    } finally {
        loadingManaged.value = false;
    }
};


</script>
